import { Client, ClientHybrid, ClientNested } from '@/models/client.model';
import { HsContact, HsDeal } from '@/models/hs-contact';
import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Binder } from 'src/app/models/binder.model';
import { environment } from 'src/environments/environment';

export interface Site {
  GroundSnowLoad: number | null,
    RoofSnowLoad: number | null,
    FrostDepth: number | null,
    SitePlot: string | null,
    Orientation: string | null,
    SiteAddress: string | null,
    SiteCity: string | null,
    SiteState: string | null,
    SiteZip: number | null,
    TaxOverride: boolean | null,
    TaxRate: number | null,
    Location_Code: number | null,
    County: string | null
}

interface returnedClient {
  clients:ClientNested,
  contacts:HsContact[],
  deals:HsDeal[],
  Clients:Partial<Client>
}

@Injectable({
  providedIn: 'root'
})
export class ClientService {
  clientData = signal<ClientHybrid[]>([]);
  clientSiteData = signal<Site>({
    GroundSnowLoad: 0,
    RoofSnowLoad: 0,
    FrostDepth: 0,
    SitePlot: '',
    Orientation: 'Standard (Garage Right)',
    SiteAddress: '',
    SiteCity: '',
    SiteState: 'WA',
    SiteZip: null,
    TaxOverride: false,
    TaxRate: 0,
    Location_Code: 0,
    County: ''
  });
  primaryData = signal<{clientId:number,lastName:string} | null>(null);

  private BASE_URL = environment.landmark_API_URL + '/clients';

  private hsId = signal<number | null>(null);


  // private newRecord = new BehaviorSubject<boolean>(false);
  // newRecord$ = this.newRecord.asObservable();

  constructor(private http: HttpClient) {
  }

  getClientGroup(clientId: number): Observable<Client[]> {
    return this.http.get<Client[]>(`${this.BASE_URL}/${clientId}`);
  }

  getClient(clientId: number): Observable<returnedClient> {
    return this.http.get<returnedClient>(`${this.BASE_URL}/fulldetails/${clientId}`);
  }

  getHsClient(hsId: number): Observable<returnedClient> {
    this.hsId.set(hsId);
    return this.http.get<returnedClient>(`${this.BASE_URL}/hsdetails/${hsId}`);
  }

  updateData(newData: ClientHybrid[]) {
    this.clientData.set(newData);
  }

  setClientData(data:ClientHybrid[]) {
    this.clientData.set(data);
    // console.log('clientData:', this.clientData());
  }

  loadHubspotClient(hsId: number) {
    return this.http.get<returnedClient>(`${this.BASE_URL}/hsdetails/${hsId}`);
  }

  getCombinedNames() {
    const names = this.clientData().map(client => client.ClientFirstName + ' ' + client.ClientLastName);
    return names.join(', ');
  }

  phoneTransform(phone: string): string {
    if (!phone) { return ''; }

    phone = phone.replace(/[^0-9]/g, '');  // remove all non-digits
    if (phone.length != 10) { return phone; } // return original input if not 10-digits

    let formattedPhone = "(" + phone.substr(0, 3) + ") " + phone.substr(3, 3) + "-" + phone.substr(6);
    
    return formattedPhone;
  }

  setClientSiteData(data:Site) {
    this.clientSiteData.set(data);
  }

  updatePrimary(clientId:number,lastName:string) {
    this.primaryData.set({clientId:clientId,lastName:lastName})
  }

  updateSelectedEmail(clientId: number, email: string) {
    const currentData = this.clientData();
    const updatedData = currentData.map(client => {
      if (client.clientId === clientId) { // Assuming ClientId is the unique identifier
        return { ...client, email: email };
      }
      return client;
    });
    this.setClientData(updatedData);
  }

  updateSelectedMode(clientId: number, mode: string) {
    const currentData = this.clientData();
    const updatedData = currentData.map(client => {
      if (client.clientId === clientId) { // Assuming ClientId is the unique identifier
        return { ...client, mode: mode };
      }
      return client;
    });
    this.setClientData(updatedData);
  }

  transformUnlinkedClientsData(id?:number, contacts?: HsContact[]) {
    let allClients: any[] = [];

    // Check if contacts are defined and not an empty array
    if (contacts && contacts.length > 0) {
      contacts.forEach(contact => {
        const formattedContact = this.formatContact(contact);
        allClients.push(formattedContact);
      });
    }

    // console.log(allClients)

    this.setClientData(allClients);
    if(id) {
      this.updatePrimary(id,allClients[0].ClientLastName)
    }
  }

  transformBinderClientsData(clients: ClientNested, id?:number, contacts?: HsContact[]) {
    // console.log('transforming clients', clients);
    // console.log('contacts', contacts);
    // console.log('id', id);
    let allClients = [];
    // console.log('transforming contacts');

    // Check if contacts are defined and not an empty array
    if (contacts && contacts.length > 0) {
      contacts.forEach(contact => {
        // console.log('contact:', contact);
        const formattedContact = this.formatContact(contact);
        allClients.push(formattedContact);
      });
    } else if (clients) {
      // Format and add the primary client with primary flag set to true
      const primaryClient = this.formatClient(clients, true);
      allClients.push(primaryClient);

      // Format and add associated clients, if any, with primary flag set to false
      if (clients.ClientJunction_ClientJunction_PrimaryClientToClients) {
        clients.ClientJunction_ClientJunction_PrimaryClientToClients.forEach(association => {
          const associatedClient = this.formatClient(association.Clients_ClientJunction_SecondaryClientToClients, false);
          allClients.push(associatedClient);
        });
      }
    }

    // console.log('allClients', allClients)

    this.setClientData(allClients);
    if(id) {
      this.updatePrimary(id,allClients[0].ClientLastName)
    }
  }

  private formatContact(contact: HsContact) {
    // Initialize an array for emails
    let emails = [];
    let phones = [];
    let smsItems = [];
    let telItems = [];
    let emailItems = [];
    let labels = [];

    // Add non-null email to the emails array
    if (contact.properties.email) {
      emails.push(contact.properties.email.trim());
      emailItems.push({
        label: 'Email ' + contact.properties.email.trim(),
        email: contact.properties.email.trim(),
        icon: 'pi pi-envelope',
        command: () => {
          this.emailLink(contact.properties.email.trim());
        }
      });
    };
    if (contact.properties.phone) {
      phones.push(this.phoneTransform(contact.properties.phone));
      smsItems.push({
        label: 'Text ' + contact.properties.phone,
        icon: 'pi pi-comment', // Adjust the icon as needed
        command: () => {
          this.smsLink(contact.properties.phone);
        }
      });
      telItems.push({
        label: 'Call ' + contact.properties.phone,
        icon: 'pi pi-phone', // Adjust the icon as needed
        command: () => {
          this.telLink(contact.properties.phone);
        }
      });
    };

    if(contact.hsLabels) {
      contact.hsLabels.filter(label => label.typeId !== 449).forEach(label => {
        labels.push({
          label: label.label,
          icon: 'pi pi-user',
          severity: 'secondary'
        });
      });
    }

    if(contact.primary) {
      labels.push({
        label: 'Primary',
        icon: 'pi pi-user',
        severity: 'secondary'
      });
    }

    // Construct and return the formatted contact object
    return {
      ClientFirstName: contact.properties.firstname,
      ClientLastName: contact.properties.lastname,
      Email: contact.properties.email,
      clientId: parseInt(contact.properties.hs_object_id),
      selectedEmail: contact.properties.email,
      mailing_address: contact.properties.mailing_address,
      emails, // Include the emails array
      phones,
      primary: contact.primary,
      smsItems,
      telItems,
      emailItems,
      labels
      // Add other properties as needed, mapping from the contact structure to your client structure
    };
  }

  private formatClient(client: ClientNested, isPrimary: boolean) {
    // Initialize arrays for phone numbers and emails
    let phones = [];
    let emails = [];
    let smsItems = [];
    let telItems = [];
    let emailItems = [];
    let labels = [];

    // Add non-null phone numbers to the phones array
    if (client.Phone) {
      phones.push(client.Phone);
      smsItems.push({
        label: 'Text ' + client.Phone,
        icon: 'pi pi-comment', // Adjust the icon as needed
        command: () => {
          this.smsLink(client.Phone);
        }
      });
      telItems.push({
        label: 'Call ' + client.Phone,
        icon: 'pi pi-phone', // Adjust the icon as needed
        command: () => {
          this.telLink(client.Phone);
        }
      });
    };
    if (client.Phone2) {
      phones.push(client.Phone2);
      smsItems.push({
        label: 'Text ' + client.Phone2,
        icon: 'pi pi-comment',
        command: () => {
          this.smsLink(client.Phone2);
        }
      });
      telItems.push({
        label: 'Call ' + client.Phone2,
        icon: 'pi pi-phone',
        command: () => {
          this.telLink(client.Phone2);
        }
      });
    };
    if (client.Phone3) {
      phones.push(client.Phone3);
      smsItems.push({
        label: 'Text ' + client.Phone3,
        icon: 'pi pi-comment',
        command: () => {
          this.smsLink(client.Phone3);
        }
      });
      telItems.push({
        label: 'Call ' + client.Phone3,
        icon: 'pi pi-phone',
        command: () => {
          this.telLink(client.Phone3);
        }
      });
    };

    // Add non-null emails to the emails array
    if (client.Email) {
      emails.push(client.Email.trim());
      emailItems.push({
        label: 'Email ' + client.Email.trim(),
        email: client.Email.trim(),
        icon: 'pi pi-envelope',
        command: () => {
          this.emailLink(client.Email.trim());
        }
      });
    };
    if (client.Email2) {
      emails.push(client.Email2.trim());
      emailItems.push({
        label: 'Email ' + client.Email2.trim(),
        email: client.Email2.trim(),
        icon: 'pi pi-envelope',
        command: () => {
          this.emailLink(client.Email2.trim());
        }
      });
    };
    if (client.Email3) {
      emails.push(client.Email3.trim());
      emailItems.push({
        label: 'Email ' + client.Email3.trim(),
        email: client.Email3.trim(),
        icon: 'pi pi-envelope',
        command: () => {
          this.emailLink(client.Email3.trim());
        }
      });
    };

    if(isPrimary) {
      labels.push({
        label: 'Primary',
        icon: 'pi pi-user',
        severity: 'secondary'
      });
    } else {
      labels.push({
        label: 'Co-Signer',
        icon: 'pi pi-user',
        severity: 'secondary'
      });
    }

    // Return the formatted client object with primary flag
    return {
      ...client,
      phones,
      emails,
      primary: isPrimary,
      smsItems,
      telItems,
      emailItems,
      mailing_address: client.MailingAddress + ' ' + client.MailingCity + ', ' + client.MailingState + ' ' + client.MailingZip,
      selectedEmail: client.Email,
      clientId: client.ClientID,
      labels
    };
  }

  private smsLink(number: string) {
    const url = 'sms:' + number;
    window.open(url, '_blank');
  }

  private telLink(number: string) {
    const url = 'tel:' + number;
    window.open(url, '_blank');
  }

  private emailLink(number: string) {
    const url = 'mailto:' + number;
    window.open(url, '_blank');
  }

}
